import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Clock, Play, Pause, Square, Users, RotateCcw } from 'lucide-react';
import GameClockTimer from './GameClockTimer';
import SportEventControls from './SportEventControls';
import PlayByPlayLogger from './PlayByPlayLogger';
import { GameRosterManagement } from './GameRosterManagement';
import { liveGameService } from '@/services/liveGameService';
import { gameRosterService, GameRosterPlayer } from '@/services/gameRosterService';
import { useToast } from '@/hooks/use-toast';
import { getSportConfig } from '@/config/sportsConfig';
import { supabase } from '@/integrations/supabase/client';

interface LiveGameScoringProps {
  game: any;
  onBack: () => void;
}

const LiveGameScoring = ({ game, onBack }: LiveGameScoringProps) => {
  const [session, setSession] = useState<any>(null);
  const [gameState, setGameState] = useState({
    homeScore: 0,
    awayScore: 0,
    currentPeriod: 1,
    timeRemaining: 0,
    isRunning: false,
    events: [] as any[]
  });
  const [loading, setLoading] = useState(true);
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [gameRoster, setGameRoster] = useState<GameRosterPlayer[]>([]);
  const { toast } = useToast();

  const loadGameRoster = async () => {
    try {
      const roster = await gameRosterService.loadGameRoster(game.id);
      setGameRoster(roster);
    } catch (error) {
      console.error('Error loading game roster:', error);
    }
  };

  useEffect(() => {
    initializeSession();
    loadGameRoster();
  }, [game]);

  const initializeSession = async () => {
    console.log('=== INITIALIZING SESSION ===');
    console.log('Game object:', game);
    console.log('Game ID:', game?.id);
    
    try {
      // Check for existing session
      console.log('Checking for existing session...');
      let existingSession = await liveGameService.getActiveSession(game.id);
      console.log('Existing session result:', existingSession);
      
      if (!existingSession) {
        console.log('No existing session found, creating new one...');
        // Create new session - use the actual authenticated user
        const currentUser = (await supabase.auth.getUser()).data.user?.id || 'a9738281-3d70-4969-951d-caca53f5fbbc'; // Use logged in user or fallback to internal user
        console.log('Creating session with gameId:', game.id, 'user:', currentUser);
        
        existingSession = await liveGameService.createSession(game.id, currentUser);
        console.log('Create session result:', existingSession);
        
        if (!existingSession) {
          throw new Error('Failed to create game session');
        }
      }

      console.log('Final session:', existingSession);
      
      if (existingSession) {
        setSession(existingSession);
        console.log('Session set successfully, updating game state...');
        
        setGameState(prev => {
          const newState = {
            ...prev,
            homeScore: existingSession.home_team_score || 0,
            awayScore: existingSession.away_team_score || 0,
            currentPeriod: existingSession.current_period || 1,
            events: existingSession.session_data?.events || []
          };
          console.log('New game state:', newState);
          return newState;
        });
        
        toast({
          title: "Session Ready",
          description: "Game session loaded successfully",
        });
      }
    } catch (error) {
      console.error('Error initializing session:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      toast({
        title: "Error",
        description: `Failed to initialize game session: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const updateScore = async (team: 'home' | 'away', points: number) => {
    console.log('updateScore called:', { team, points, session: !!session, sessionId: session?.id });
    
    if (!session) {
      console.error('No session available for scoring');
      toast({
        title: "Error",
        description: "No game session available",
        variant: "destructive"
      });
      return;
    }

    const currentScore = team === 'home' ? gameState.homeScore : gameState.awayScore;
    const newScore = Math.max(0, currentScore + points);

    console.log('Score update calculation:', { 
      team, 
      currentScore, 
      points, 
      newScore,
      gameState: gameState
    });

    // Update local state immediately for responsiveness
    const newState = {
      ...gameState,
      [team === 'home' ? 'homeScore' : 'awayScore']: newScore
    };

    console.log('New game state will be:', newState);
    setGameState(newState);

    try {
      // Update database with the correct scores
      const finalHomeScore = team === 'home' ? newScore : gameState.homeScore;
      const finalAwayScore = team === 'away' ? newScore : gameState.awayScore;
      
      console.log('Updating database with scores:', { 
        sessionId: session.id,
        finalHomeScore, 
        finalAwayScore 
      });

      const success = await liveGameService.updateGameScore(
        session.id,
        finalHomeScore,
        finalAwayScore
      );

      console.log('Database update result:', success);

      if (success) {
        toast({
          title: "Score Updated",
          description: `${team === 'home' ? 'Home' : 'Away'} team: +${points} points`,
        });

        // Log the scoring event
        const event = {
          id: Date.now(),
          type: 'score',
          team,
          points,
          time: Date.now(),
          period: gameState.currentPeriod,
          newScore: newScore,
          label: `${team === 'home' ? 'Home' : 'Away'} +${points}`
        };

        console.log('Adding event:', event);
        addEvent(event);
      } else {
        throw new Error('Database update failed');
      }
    } catch (error) {
      console.error('Error updating score:', error);
      
      // Revert local state on error
      setGameState(gameState);
      
      toast({
        title: "Error",
        description: "Failed to update score. Please try again.",
        variant: "destructive"
      });
    }
  };

  const addEvent = (event: any) => {
    console.log('Adding event to LiveGameScoring:', event);
    const newEvents = [...gameState.events, { ...event, id: Date.now() }];
    setGameState(prev => ({ ...prev, events: newEvents }));
    
    // Success feedback
    toast({
      title: "Event Logged",
      description: `${event.label} has been recorded`,
    });
  };

  const onClockUpdate = (timeRemaining: number, isRunning: boolean) => {
    setGameState(prev => ({ ...prev, timeRemaining, isRunning }));
  };

  const onPeriodChange = (period: number) => {
    setGameState(prev => ({ ...prev, currentPeriod: period }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Clock className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading game session...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground">{game.game_title || game.game_name}</h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="secondary">{game.sport_name}</Badge>
              <span>Game Code: {game.game_code}</span>
              <span>{new Date(game.game_date).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Scoreboard */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-center text-foreground">Live Scoreboard</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 items-center">
              {/* Home Team */}
              <div className="text-center">
                {(game.home_team_logo || game.homeTeamLogo) && (
                  <img 
                    src={game.home_team_logo || game.homeTeamLogo} 
                    alt="Home team logo" 
                    className="w-12 h-12 mx-auto mb-2 rounded-full object-cover"
                  />
                )}
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {game.home_team_name || game.homeTeamName || game.home_team || 'Home Team'}
                </h3>
                <div className="text-4xl font-bold text-foreground mb-4">
                  {gameState.homeScore}
                </div>
                <div className="space-y-2">
                  {getSportConfig(game.sport_name || game.sportName || 'Basketball').scoringOptions.map((points) => (
                    <Button 
                      key={`home-${points}`}
                      onClick={() => {
                        console.log('Home team button clicked:', points);
                        console.log('Current session state:', !!session);
                        console.log('Session object:', session);
                        updateScore('home', points);
                      }}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                      size="sm"
                      disabled={!session}
                    >
                      +{points}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Game Clock */}
              <div className="text-center border-x border-border px-4">
                <GameClockTimer
                  sportName={game.sport_name || game.sportName || 'Basketball'}
                  currentPeriod={gameState.currentPeriod}
                  onClockUpdate={onClockUpdate}
                  onPeriodChange={onPeriodChange}
                />
              </div>

              {/* Away Team */}
              <div className="text-center">
                {(game.away_team_logo || game.awayTeamLogo) && (
                  <img 
                    src={game.away_team_logo || game.awayTeamLogo} 
                    alt="Away team logo" 
                    className="w-12 h-12 mx-auto mb-2 rounded-full object-cover"
                  />
                )}
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {game.away_team_name || game.awayTeamName || game.away_team || 'Away Team'}
                </h3>
                <div className="text-4xl font-bold text-foreground mb-4">
                  {gameState.awayScore}
                </div>
                <div className="space-y-2">
                  {getSportConfig(game.sport_name || game.sportName || 'Basketball').scoringOptions.map((points) => (
                    <Button 
                      key={`away-${points}`}
                      onClick={() => {
                        console.log('Away team button clicked:', points);
                        console.log('Current session state:', !!session);
                        console.log('Session object:', session);
                        updateScore('away', points);
                      }}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                      size="sm"
                      disabled={!session}
                    >
                      +{points}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Game Controls */}
        <Tabs defaultValue="events" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="events">Game Events</TabsTrigger>
            <TabsTrigger value="stats">Player Stats</TabsTrigger>
            <TabsTrigger value="log">Play by Play</TabsTrigger>
          </TabsList>
          
          <TabsContent value="events">
            <SportEventControls
              sportName={game.sport_name || game.sportName || 'Basketball'}
              onEvent={addEvent}
              game={game}
              gameRoster={gameRoster}
            />
          </TabsContent>
          
          <TabsContent value="stats">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <Users className="h-5 w-5" />
                  Player Statistics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Home Team Players */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-foreground flex items-center gap-2">
                      {game.home_team_logo && (
                        <img 
                          src={game.home_team_logo} 
                          alt="Home team logo" 
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      )}
                      {game.home_team_name || 'Home Team'}
                    </h4>
                    <div className="text-sm text-muted-foreground">
                      Player roster will load here when available
                    </div>
                  </div>
                  
                  {/* Away Team Players */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-foreground flex items-center gap-2">
                      {game.away_team_logo && (
                        <img 
                          src={game.away_team_logo} 
                          alt="Away team logo" 
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      )}
                      {game.away_team_name || 'Away Team'}
                    </h4>
                    <div className="text-sm text-muted-foreground">
                      Player roster will load here when available
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="log">
            <PlayByPlayLogger events={gameState.events} />
          </TabsContent>
        </Tabs>
      </div>

      <GameRosterManagement
        isOpen={showRosterModal}
        onClose={() => setShowRosterModal(false)}
        gameId={game.id}
        game={game}
        onRosterUpdate={loadGameRoster}
      />
    </div>
  );
};

export default LiveGameScoring;
